/**
 * WebSocket client for real-time messaging
 * 
 * Adapted minimal WebSocket pattern to work with our FastAPI backend.
 * Provides clean separation from React component lifecycle.
 */

import { debugWS, warnWS, errorWS, logWSLifecycle, logWSMessage, debugWSMessaging, warnWSMessaging, errorWSMessaging } from './debugWS';
import { WS_BACKEND_URL } from '../config';

let socket = null;
let currentRoomId = null;
let currentUser = null;
let currentSession = null;
let messageHandlers = new Set();

// Expose WebSocket instance for typing indicators
if (typeof window !== 'undefined') {
  window.__getWebSocketInstance = () => socket;
}

// Guard to prevent multiple simultaneous connection attempts
let connectionInProgress = false;

// Handler reference for the browser 'online' event so we can remove it on disconnect
let onlineHandler = null;

// Handler reference for the document visibility change event so we can remove it on disconnect.
// Detects when the user returns to the tab after switching apps — the browser throttles
// setInterval while backgrounded, so the heartbeat may not fire, leaving the socket in a
// zombie state (server-side eviction while client readyState is still OPEN).
let visibilityHandler = null;

// Track whether we have ever successfully connected in this session.
// Used to distinguish initial connect from reconnect so we can request
// missed messages on reconnect without firing on first load.
let wasEverConnected = false;

// Heartbeat configuration and variables
const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const HEARTBEAT_TIMEOUT = 10000; // 10 seconds to wait for pong
let heartbeatInterval = null;
let heartbeatTimeout = null;

// Store event handlers for proper cleanup
let registeredHandlers = {
  onopen: null,
  onmessage: null,
  onerror: null,
  onclose: null
};
let reconnectAttempts = 0;
const maxReconnectAttempts = 10;
let isManuallyConnecting = false; // Flag to prevent auto-reconnect during manual connections

// Get backend URL from centralized config
const getBackendWsUrl = () => {
  return WS_BACKEND_URL;
};

// Get correct WebSocket path for environment
const getWsPath = (roomId) => {
  // Use consistent path across all environments - platform handles routing
  return `/api/ws-v2/${roomId}`;
};

/**
 * Enhanced WebSocket connection function with StrictMode guards
 */
async function connectToWebSocket(roomId, user, session) {
  debugWSMessaging('🔗 ========== WebSocket Connection Request ==========');
  debugWSMessaging('🔗 Room ID:', roomId);
  debugWSMessaging('🔗 User type:', user?.is_guest ? 'guest' : 'authenticated');
  debugWSMessaging('🔗 Connection in progress:', connectionInProgress);

  // Guard against multiple simultaneous connection attempts (StrictMode protection)
  if (connectionInProgress) {
    debugWSMessaging('⚠️ Connection already in progress, skipping duplicate request');
    return;
  }

  connectionInProgress = true;
  
  try {
    await _performConnection(roomId, user, session);
  } finally {
    connectionInProgress = false;
  }
}

/**
 * Internal connection function
 */
async function _performConnection(roomId, user, session) {
  isManuallyConnecting = true;
  
  debugWSMessaging('🔗 Checking existing connection...');
  
  // Check if we need to close existing connection
  if (socket) {
    const currentState = socket.readyState;
    debugWSMessaging('🔗 Existing socket state:', currentState);
    
    if (currentState === WebSocket.CONNECTING) {
      debugWSMessaging('🔗 Socket currently connecting, waiting for completion...');
      return new Promise(resolve => {
        const checkConnection = () => {
          if (!socket || socket.readyState === WebSocket.CLOSED) {
            _performConnection(roomId, user, session);
            resolve();
          } else if (socket.readyState === WebSocket.OPEN) {
            debugWSMessaging('🔗 Connection completed, proceeding...');
            _performConnection(roomId, user, session);
            resolve();
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }
    
    if (currentState === WebSocket.OPEN) {
      debugWSMessaging('🔄 Closing existing open connection before creating new one');
      _cleanupWebSocket();
      
      return new Promise(resolve => {
        setTimeout(() => {
          _performConnection(roomId, user, session);
          resolve();
        }, 150);
      });
    } else if (currentState === WebSocket.CLOSED || currentState === WebSocket.CLOSING) {
      debugWSMessaging('🧹 Clearing already closed/closing socket', `(state: ${currentState})`);
      _cleanupWebSocket();
    }
  }

  // Update context before creating new connection
  currentRoomId = roomId;
  currentUser = user;
  currentSession = session;

  // Register the browser 'online' event handler once per session.
  // When WiFi drops, the reconnect loop can exhaust its attempts before
  // the network recovers. The 'online' event gives us a guaranteed trigger
  // to reconnect the moment the browser sees connectivity restored.
  if (!onlineHandler) {
    onlineHandler = () => {
      warnWSMessaging('🌐 Network restored — attempting WebSocket reconnect');
      reconnectAttempts = 0; // Reset so the backoff loop gets fresh attempts
      if (currentRoomId && currentUser && currentSession && !isConnected()) {
        _performConnection(currentRoomId, currentUser, currentSession);
      }
    };
    window.addEventListener('online', onlineHandler);
    debugWSMessaging('🌐 Registered online event handler');
  }

  if (!visibilityHandler) {
    visibilityHandler = () => {
      if (document.hidden || !currentRoomId || !currentUser) return;

      if (!isConnected()) {
        // Socket is dead — reconnect immediately instead of waiting for the next heartbeat cycle
        warnWSMessaging('👁️ Tab visible — socket dead, reconnecting immediately');
        reconnectAttempts = 0;
        _performConnection(currentRoomId, currentUser, currentSession);
      } else {
        // Socket appears open but may be a zombie (server evicted it while tab was hidden).
        // Send an immediate ping; if no pong arrives within HEARTBEAT_TIMEOUT, close and reconnect.
        debugWSMessaging('👁️ Tab visible — pinging to verify connection health');
        try {
          socket.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
          if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
          heartbeatTimeout = setTimeout(() => {
            warnWS('👁️ Visibility ping timed out — closing zombie socket to force reconnect');
            if (socket) socket.close();
          }, HEARTBEAT_TIMEOUT);
        } catch (e) {
          warnWS('👁️ Visibility ping send failed — closing socket to force reconnect');
          if (socket) socket.close();
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    debugWSMessaging('👁️ Registered visibility change handler');
  }

  if (!roomId || !session?.access_token) {
    errorWSMessaging('❌ Missing required connection parameters');
    isManuallyConnecting = false;
    return;
  }

  const wsUrl = `${getBackendWsUrl()}${getWsPath(roomId)}?token=${session.access_token}`;
  debugWSMessaging('🚀 Creating new WebSocket connection to:', wsUrl.replace(/token=[^&]*/, 'token=***'));
  debugWSMessaging('🎯 Environment-specific path:', getWsPath(roomId));
  debugWSMessaging('🔧 BACKEND_WS_URL from config:', getBackendWsUrl());
  debugWSMessaging('🔧 Full WebSocket URL (with token):', wsUrl);
  console.log('🔧 WebSocket Debug - BACKEND_WS_URL:', getBackendWsUrl());
  console.log('🔧 WebSocket Debug - Full URL:', wsUrl);

  try {
    socket = new WebSocket(wsUrl);
    console.log("🧠 WS version:", socket.url.includes("ws-v2") ? "v2" : "LEGACY");
    _setupEventHandlers();
  } catch (error) {
    errorWSMessaging('❌ Failed to create WebSocket:', error);
    isManuallyConnecting = false;
    notifyHandlers({ type: 'connection_error', error: 'Failed to create WebSocket connection' });
  }
}

/**
 * Setup WebSocket event handlers with proper cleanup support
 */
function _setupEventHandlers() {
  if (!socket) return;

  // Create handler functions that can be properly removed
  registeredHandlers.onopen = () => {
    debugWSMessaging('✅ Connected (readyState = 1)');
    debugWSMessaging('🔌 WebSocket URL:', socket.url);
    debugWSMessaging('🔌 WebSocket readyState:', socket.readyState);
    debugWSMessaging('🔌 WebSocket protocol:', socket.protocol);
    reconnectAttempts = 0;
    isManuallyConnecting = false;
    notifyHandlers({ type: 'connection_status', connected: true });

    // Start heartbeat when connection is established
    startHeartbeat();
    debugWSMessaging('🏓 Heartbeat started');

    // On reconnect (not initial connect), notify ChatRoom so it can
    // request any messages missed during the disconnection window.
    if (wasEverConnected) {
      notifyHandlers({ type: 'reconnected' });
    }
    wasEverConnected = true;
  };

  registeredHandlers.onmessage = (event) => {
    debugWSMessaging('📨 RAW MESSAGE RECEIVED:');
    debugWSMessaging('📨 Event type:', typeof event);
    debugWSMessaging('📨 Event data type:', typeof event.data);
    debugWSMessaging('📨 Event data length:', event.data?.length);
    debugWSMessaging('📨 Raw event data:', event.data);

    try {
      // C-proto: data is typed as ServerWSEvent (see frontend/src/proto/interfaces.ts)
      /** @type {import('../proto/interfaces').ServerWSEvent} */
      const data = JSON.parse(event.data);
      debugWSMessaging('📨 PARSED MESSAGE DATA:');
      debugWSMessaging('📨 Message type:', data.type);
      debugWSMessaging('📨 Full parsed data:', JSON.stringify(data, null, 2));
      
      // Handle specific message types with detailed logging
      if (data.type === 'connection_established') {
        logWSMessage('🏠 Connection established for room:', data.room_id);
      } else if (data.type === 'user_joined') {
        logWSMessage('👋 User joined room:', data.user_name);
      } else if (data.type === 'ping') {
        logWSMessage('🏓 Ping received → Sending pong');
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'pong', timestamp: new Date().toISOString() }));
          debugWS('📤 Pong sent');
        }
      } else if (data.type === 'pong') {
        logWSMessage('🏓 Pong received from server');
        handlePong();
      } else {
        debugWSMessaging('📨 FORWARDING MESSAGE TO HANDLERS:');
        debugWSMessaging('📨 Handler count:', messageHandlers.size);
        debugWSMessaging('📨 Message being forwarded:', JSON.stringify(data, null, 2));
        notifyHandlers(data);
      }
    } catch (error) {
      errorWSMessaging('❌ Failed to parse WebSocket message:', error);
      errorWSMessaging('❌ Raw data was:', event.data);
    }
  };

  registeredHandlers.onerror = (error) => {
    errorWSMessaging('❌ WebSocket Error:', error);
    notifyHandlers({ type: 'connection_error', error });
  };

  registeredHandlers.onclose = (event) => {
    warnWSMessaging('🔌 Disconnected:', event.code, event.reason);
    
    // Stop heartbeat when connection closes
    stopHeartbeat();
    debugWSMessaging('🏓 Heartbeat stopped');
    
    notifyHandlers({ type: 'connection_status', connected: false });
    
    if (!isManuallyConnecting) {
      if (reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 30000);
        warnWSMessaging(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
        
        setTimeout(() => {
          if (currentRoomId && currentUser && currentSession) {
            _performConnection(currentRoomId, currentUser, currentSession);
          }
        }, delay);
      } else {
        errorWSMessaging(`❌ Max reconnection attempts (${maxReconnectAttempts}) reached`);
        notifyHandlers({ type: 'connection_error', error: 'Max reconnection attempts reached' });
      }
    }
  };

  // Use addEventListener for proper cleanup support
  socket.addEventListener('open', registeredHandlers.onopen);
  socket.addEventListener('message', registeredHandlers.onmessage);
  socket.addEventListener('error', registeredHandlers.onerror);
  socket.addEventListener('close', registeredHandlers.onclose);
  
  debugWSMessaging('✅ Event handlers registered with addEventListener');
}

/**
 * Clean up WebSocket connection and event handlers
 */
function _cleanupWebSocket() {
  // Stop heartbeat before cleanup
  stopHeartbeat();
  
  if (socket && registeredHandlers.onopen) {
    debugWSMessaging('🧹 Cleaning up WebSocket event handlers...');
    
    // Remove event listeners
    socket.removeEventListener('open', registeredHandlers.onopen);
    socket.removeEventListener('message', registeredHandlers.onmessage);
    socket.removeEventListener('error', registeredHandlers.onerror);  
    socket.removeEventListener('close', registeredHandlers.onclose);
    
    // Clear handler references
    registeredHandlers.onopen = null;
    registeredHandlers.onmessage = null;
    registeredHandlers.onerror = null;
    registeredHandlers.onclose = null;
    
    debugWSMessaging('✅ Event handlers cleaned up');
  }
  
  if (socket) {
    try {
      socket.close();
    } catch (closeError) {
      debugWSMessaging('⚠️ Error closing socket:', closeError);
    }
    socket = null;
  }
}

/**
 * Send message via WebSocket (not HTTP API) - Phase 1 migration to WebSocket-only messaging
 */
async function sendMessage(content, api) {
  if (!currentRoomId || !currentUser || !currentSession) {
    warnWS('Cannot send message - missing context');
    return false;
  }

  if (!socket || socket.readyState !== WebSocket.OPEN) {
    errorWSMessaging('Cannot send message - WebSocket not connected');
    return false;
  }

  try {
    debugWSMessaging('📤 ========== Sending Message via WebSocket ==========');
    debugWSMessaging('📤 Room ID:', currentRoomId);
    debugWSMessaging('📤 Content preview:', content.substring(0, 50) + '...');
    debugWSMessaging('📤 User type:', currentUser.is_guest ? 'guest' : 'authenticated');
    
    // Prepare WebSocket message data
    const wsMessageData = {
      type: "chat_message",
      content: content.trim(),
      room_id: currentRoomId,
      sender_type: 'user',
      timestamp: new Date().toISOString()
    };

    // Add user identification for logging
    if (currentUser.is_guest || currentUser.guest_id) {
      wsMessageData.guest_user_id = currentUser.id || currentUser.guest_id;
      debugWSMessaging('📤 Guest user ID:', wsMessageData.guest_user_id);
    } else {
      debugWSMessaging('📤 Authenticated user (token-based identification)');
    }

    debugWSMessaging('📤 Complete WebSocket message data:', wsMessageData);
    debugWSMessaging('📤 Sending via WebSocket connection...');
    
    // Send via WebSocket
    socket.send(JSON.stringify(wsMessageData));
    
    debugWSMessaging('✅ Message sent successfully via WebSocket');
    debugWSMessaging('📤 ========== WebSocket Message Send Complete ==========');
    return true;
    
  } catch (error) {
    errorWSMessaging('❌ Error sending message via WebSocket:', error);
    errorWSMessaging('📤 ========== WebSocket Message Send Failed ==========');
    return false;
  }
}

/**
 * Register a message handler function
 */
function addMessageHandler(handler) {
  messageHandlers.add(handler);
  debugWS('📝 Message handler registered, total:', messageHandlers.size);
}

/**
 * Unregister a message handler function
 */
function removeMessageHandler(handler) {
  messageHandlers.delete(handler);
  debugWS('🗑️ Message handler removed, remaining:', messageHandlers.size);
}

/**
 * Get current handler count (for debugging)
 */
function getHandlerCount() {
  return messageHandlers.size;
}

/**
 * Clear all message handlers (for debugging duplicate issues)
 */
function clearAllHandlers() {
  const count = messageHandlers.size;
  messageHandlers.clear();
  debugWS(`🧹 Cleared ${count} message handlers`);
}

/**
 * Notify all message handlers of a new message with enhanced debugging
 */
function notifyHandlers(message) {
  debugWSMessaging('🔔 NOTIFY HANDLERS CALLED:');
  debugWSMessaging('🔔 Handler count:', messageHandlers.size);
  debugWSMessaging('🔔 Message type:', message?.type);
  debugWSMessaging('🔔 Message content preview:', JSON.stringify(message, null, 2));

  if (messageHandlers.size === 0) {
    warnWSMessaging('⚠️ No message handlers registered to receive message');
    return;
  }

  let handlerCount = 0;
  messageHandlers.forEach((handler, index) => {
    try {
      debugWSMessaging(`🔔 Calling handler #${handlerCount} of ${messageHandlers.size}`);
      handler(message);
      debugWSMessaging(`✅ Handler #${handlerCount} completed successfully`);
      handlerCount++;
    } catch (error) {
      errorWSMessaging(`❌ Handler #${handlerCount} failed:`, error);
      errorWSMessaging(`❌ Message was:`, message);
      handlerCount++;
    }
  });

  debugWSMessaging(`🔔 Notified ${handlerCount} handlers total`);
}

/**
 * Disconnect WebSocket with proper cleanup
 */
function disconnect() {
  debugWSMessaging('🔌 Disconnecting WebSocket...');
  reconnectAttempts = maxReconnectAttempts; // Prevent reconnection
  _cleanupWebSocket();

  // Remove the online event handler so it doesn't fire after intentional disconnect
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler);
    onlineHandler = null;
    debugWSMessaging('🌐 Removed online event handler');
  }

  // Remove the visibility change handler
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
    debugWSMessaging('👁️ Removed visibility change handler');
  }

  // Clear context
  currentRoomId = null;
  currentUser = null;
  currentSession = null;
  wasEverConnected = false;

  debugWSMessaging('✅ WebSocket disconnected and cleaned up');
  
  // Notify handlers
  notifyHandlers({ type: 'connection_status', connected: false });
}

/**
 * Get current connection status
 */
function isConnected() {
  return socket && socket.readyState === WebSocket.OPEN;
}

/**
 * Get current connection state info
 */
function getConnectionInfo() {
  return {
    connected: isConnected(),
    roomId: currentRoomId,
    userId: currentUser?.id,
    reconnectAttempts,
    handlerCount: messageHandlers.size
  };
}

/**
 * Start heartbeat to keep connection alive
 */
function startHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }
  
  debugWS('🏓 Starting heartbeat with 30s interval');
  
  heartbeatInterval = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      debugWS('🏓 Sending ping to server');
      socket.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
      
      // Set timeout to wait for pong response
      if (heartbeatTimeout) {
        clearTimeout(heartbeatTimeout);
      }
      
      heartbeatTimeout = setTimeout(() => {
        warnWS('🏓 Pong timeout - closing dead connection to trigger reconnect');
        if (socket) {
          socket.close();
        }
      }, HEARTBEAT_TIMEOUT);
    } else {
      debugWS('🏓 Skipping ping - WebSocket not open');
    }
  }, HEARTBEAT_INTERVAL);
}

/**
 * Stop heartbeat
 */
function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
  if (heartbeatTimeout) {
    clearTimeout(heartbeatTimeout);
    heartbeatTimeout = null;
  }
}

/**
 * Send a raw JSON payload over the WebSocket.
 * Used for control messages (e.g. sync_missed_messages) that are not
 * chat messages and don't need the chat-message wrapping in sendMessage().
 */
function sendRaw(payload) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(payload));
    return true;
  }
  debugWSMessaging('⚠️ sendRaw: socket not open, payload dropped:', payload?.type);
  return false;
}

/**
 * Handle pong response
 */
function handlePong() {
  debugWS('💚 Pong received - connection is healthy');
  if (heartbeatTimeout) {
    clearTimeout(heartbeatTimeout);
    heartbeatTimeout = null;
  }
}

export {
  connectToWebSocket,
  sendMessage,
  sendRaw,
  addMessageHandler,
  removeMessageHandler,
  disconnect,
  isConnected,
  getConnectionInfo,
  getHandlerCount,
  clearAllHandlers,
  startHeartbeat,
  stopHeartbeat,
  handlePong
};